import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useFormContext } from '../contexts/FormContext';
import { Card, CardContent } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { HelpCircle, Upload, X, Image } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { musicStyles, languages, ambiances, eras } from '../data/mockData';
import { useToast } from '../hooks/use-toast';
import VoiceRecorder from './VoiceRecorder';

const SongForm = () => {
  const { language, t } = useLanguage();
  const { toast } = useToast();
  const { currentStep, setCurrentStep } = useFormContext(); // Utiliser le contexte pour gérer les étapes
  const [formData, setFormData] = useState({
    whoFor: '',
    relationship: '',
    hasOccasion: false,
    occasion: '',
    story: '',
    email: '',
    songLanguage: 'en',
    musicalStyle: '',
    era: '',
    ambiance: '',
    voice: 'male',
    verses: '2',
    hasPhrase: false,
    phrase: '',
    streaming: false,
    delivery: 'standard',
    hasPhoto: false,
    photo: null,
    hasVoiceRecording: false,
    voiceRecordings: [
      { owner: '', recording: null },
      { owner: '', recording: null }
    ],
    hasAiClip: false,
    aiClipPhotos: [
      // Initialiser avec 3 slots vides minimum
      { context: '', file: null, preview: null },
      { context: '', file: null, preview: null },
      { context: '', file: null, preview: null }
    ],
    aiClipScenario: ''
  });

  const addPhotoSlot = () => {
    if (formData.aiClipPhotos.length < 10) {
      setFormData({
        ...formData,
        aiClipPhotos: [...formData.aiClipPhotos, { context: '', file: null, preview: null }]
      });
    }
  };

  const removePhotoSlot = (index) => {
    const updatedPhotos = formData.aiClipPhotos.filter((_, i) => i !== index);
    setFormData({ ...formData, aiClipPhotos: updatedPhotos });
  };

  const updatePhotoContext = (index, context) => {
    const updatedPhotos = [...formData.aiClipPhotos];
    updatedPhotos[index].context = context;
    setFormData({ ...formData, aiClipPhotos: updatedPhotos });
  };

  const handlePhotoUpload = (index, file) => {
    if (!file) return;

    // Validate file size
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: `${file.name} exceeds 10MB`,
        variant: "destructive"
      });
      return;
    }

    // Update specific photo slot
    const updatedPhotos = [...formData.aiClipPhotos];
    updatedPhotos[index] = {
      ...updatedPhotos[index],
      file: file,
      preview: URL.createObjectURL(file)
    };
    setFormData({ ...formData, aiClipPhotos: updatedPhotos });
  };

  const removePhoto = (index) => {
    const updatedPhotos = [...formData.aiClipPhotos];
    updatedPhotos[index] = {
      ...updatedPhotos[index],
      file: null,
      preview: null
    };
    setFormData({ ...formData, aiClipPhotos: updatedPhotos });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.whoFor || !formData.relationship || !formData.story || !formData.email || !formData.songLanguage || !formData.musicalStyle || !formData.voice) {
      toast({
        title: "Missing Required Fields",
        description: "Please fill in all required fields marked with *",
        variant: "destructive"
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address",
        variant: "destructive"
      });
      return;
    }

    // Show loading toast
    toast({
      title: "Processing your order...",
      description: "Uploading files and redirecting to payment",
    });

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Step 1: Upload photo if exists
      let photoUrl = '';
      if (formData.hasPhoto && formData.photo) {
        const photoFormData = new FormData();
        photoFormData.append('file', formData.photo);
        const photoResponse = await fetch(`${API_URL}/api/upload-file`, {
          method: 'POST',
          body: photoFormData
        });
        const photoData = await photoResponse.json();
        photoUrl = photoData.url;
      }

      // Step 2: Upload voice recordings if exist
      let voiceUrls = [];
      let voiceOwners = [];
      if (formData.hasVoiceRecording) {
        for (const voiceRec of formData.voiceRecordings) {
          if (voiceRec.recording) {
            const voiceFormData = new FormData();
            voiceFormData.append('file', voiceRec.recording);
            const voiceResponse = await fetch(`${API_URL}/api/upload-file`, {
              method: 'POST',
              body: voiceFormData
            });
            const voiceData = await voiceResponse.json();
            voiceUrls.push(voiceData.url);
            voiceOwners.push(voiceRec.owner || 'Voice Recording');
          }
        }
      }

      // Step 3: Upload AI Clip photos if exist
      let clipPhotoUrls = [];
      let clipPhotoContexts = [];
      if (formData.hasAiClip) {
        for (const photo of formData.aiClipPhotos) {
          if (photo.file) {
            const clipFormData = new FormData();
            clipFormData.append('file', photo.file);
            const clipResponse = await fetch(`${API_URL}/api/upload-file`, {
              method: 'POST',
              body: clipFormData
            });
            const clipData = await clipResponse.json();
            clipPhotoUrls.push(clipData.url);
            clipPhotoContexts.push(photo.context || '');
          }
        }
      }

      // Step 4: Prepare complete order data
      const orderData = {
        whoFor: formData.whoFor,
        relationship: formData.relationship,
        hasOccasion: formData.hasOccasion,
        occasion: formData.occasion,
        story: formData.story,
        email: formData.email,
        songLanguage: formData.songLanguage,
        musicalStyle: formData.musicalStyle,
        era: formData.era,
        ambiance: formData.ambiance,
        voice: formData.voice,
        verses: formData.verses,
        hasPhrase: formData.hasPhrase,
        phrase: formData.phrase,
        streaming: formData.streaming,
        delivery: formData.delivery,
        hasPhoto: formData.hasPhoto,
        photoUrl: photoUrl,
        hasVoiceRecording: formData.hasVoiceRecording,
        voiceRecordingUrls: voiceUrls.join(','),
        voiceOwners: voiceOwners.join(','),
        hasAiClip: formData.hasAiClip,
        aiClipPhotoUrls: clipPhotoUrls.join(','),
        aiClipPhotoContexts: clipPhotoContexts.join('|||'),
        aiClipScenario: formData.aiClipScenario,
        totalPrice: calculatePrice()
      };

      // Step 5: Create Stripe checkout session
      const checkoutResponse = await fetch(`${API_URL}/api/checkout/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          order_data: orderData,
          origin_url: window.location.origin
        })
      });

      if (!checkoutResponse.ok) {
        throw new Error('Failed to create checkout session');
      }

      const checkoutData = await checkoutResponse.json();

      // Step 6: Redirect to Stripe checkout
      if (checkoutData.url) {
        window.location.href = checkoutData.url;
      } else {
        throw new Error('No checkout URL received');
      }
      
    } catch (error) {
      console.error('Order submission error:', error);
      toast({
        title: "Order Failed",
        description: error.message || "Something went wrong. Please try again.",
        variant: "destructive"
      });
    }
  };

  const getLabel = (item) => {
    const labelKey = `label${language.charAt(0).toUpperCase() + language.slice(1)}`;
    return item[labelKey] || item.label;
  };

  const calculatePrice = () => {
    let price = 35.00;
    if (formData.voice === 'duo') price += 9.90;
    if (formData.verses === '3') price += 3.90;
    if (formData.verses === '4') price += 6.90;
    if (formData.hasPhrase) price += 2.90;
    if (formData.streaming) price += 9.90;
    if (formData.delivery === 'express') price += 7.90;
    if (formData.delivery === 'priority') price += 12.90;
    if (formData.hasPhoto) price += 2.90;
    if (formData.hasVoiceRecording) price += 14.00;
    if (formData.hasAiClip) price += 99.00;
    return price.toFixed(2);
  };

  return (
    <section id="create" className="py-16 bg-gradient-to-br from-orange-100 via-pink-100 to-rose-200">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
            {t('form.title').split(' ').map((word, i) => {
              const highlight = ['Personalize', 'Personnalisez', 'Personaliza', 'Personalizza'];
              return highlight.includes(word) ? 
                <span key={i} className="bg-gradient-to-r from-purple-600 via-violet-500 to-fuchsia-500 bg-clip-text text-transparent">{word} </span> : 
                word + ' ';
            })}
          </h2>
          
          {/* Quick Badge - 4 minutes */}
          <div className="inline-block mb-6">
            <span className="px-4 py-2 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-full text-sm font-semibold text-purple-700 shadow-sm">
              {t('form.quickBadge')}
            </span>
          </div>
          
          <p className="text-gray-600 max-w-2xl mx-auto">
            {t('form.subtitle')}
          </p>
          <p className="text-sm text-gray-500 mt-4">
            {t('form.required')}
          </p>
        </div>

        {/* Progress Bar */}
        <div className="max-w-3xl mx-auto mb-8">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${currentStep === 1 ? 'bg-gradient-to-r from-purple-600 to-fuchsia-500 text-white' : 'bg-green-500 text-white'}`}>
                {currentStep === 1 ? '1' : '✓'}
              </div>
              <span className={`text-sm font-semibold ${currentStep === 1 ? 'text-purple-600' : 'text-gray-600'}`}>
                Personnalisation
              </span>
            </div>
            <div className="flex-1 h-1 mx-4 bg-gray-300 rounded-full overflow-hidden">
              <div className={`h-full bg-gradient-to-r from-purple-600 to-fuchsia-500 transition-all duration-500 ${currentStep === 2 ? 'w-full' : 'w-0'}`}></div>
            </div>
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${currentStep === 2 ? 'bg-gradient-to-r from-purple-600 to-fuchsia-500 text-white' : 'bg-gray-300 text-gray-600'}`}>
                2
              </div>
              <span className={`text-sm font-semibold ${currentStep === 2 ? 'text-purple-600' : 'text-gray-600'}`}>
                Finalisation
              </span>
            </div>
          </div>
          <p className="text-center text-sm text-gray-600">
            {currentStep === 1 ? '🎵 Créez votre chanson unique' : '✅ Dernière étape avant votre chanson !'}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* ÉTAPE 1 : Personnalisation */}
          {currentStep === 1 && (
          <div className="grid lg:grid-cols-1 gap-8 animate-fadeIn">
            {/* Main Form Card */}
            <Card className="glass-card soft-shadow-lg">
              <CardContent className="p-6 md:p-8">
                <div className="space-y-6">
                  {/* Who For */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Label htmlFor="whoFor" className="text-base font-semibold">
                        {t('form.whoFor')} *
                      </Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <HelpCircle className="w-4 h-4 text-gray-400" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs">{t('form.whoForHelp')}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <Input
                      id="whoFor"
                      placeholder={t('common.nameOrNamesPlaceholder')}
                      value={formData.whoFor}
                      onChange={(e) => setFormData({ ...formData, whoFor: e.target.value })}
                      className="border-gray-300 focus:border-purple-500 focus:ring-purple-500"
                    />
                  </div>

                  {/* Relationship */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Label htmlFor="relationship" className="text-base font-semibold">
                        {t('form.relationship')} *
                      </Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <HelpCircle className="w-4 h-4 text-gray-400" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs">{t('form.relationshipHelp')}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <Input
                      id="relationship"
                      placeholder={t('common.relationshipPlaceholder')}
                      value={formData.relationship}
                      onChange={(e) => setFormData({ ...formData, relationship: e.target.value })}
                      className="border-gray-300 focus:border-purple-500 focus:ring-purple-500"
                    />
                  </div>

                  {/* Occasion */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Label className="text-base font-semibold">
                        {t('form.occasion')}
                      </Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <HelpCircle className="w-4 h-4 text-gray-400" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs">{t('form.occasionHelp')}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={formData.hasOccasion}
                        onCheckedChange={(checked) => setFormData({ ...formData, hasOccasion: checked })}
                      />
                      <span className="text-sm text-gray-600">{formData.hasOccasion ? 'Yes' : t('form.occasionNo')}</span>
                    </div>
                    {formData.hasOccasion && (
                      <Input
                        placeholder={t('common.occasionPlaceholder')}
                        value={formData.occasion}
                        onChange={(e) => setFormData({ ...formData, occasion: e.target.value })}
                        className="mt-3 border-gray-300 focus:border-purple-500 focus:ring-purple-500"
                      />
                    )}
                  </div>

                  {/* Tell Us About */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Label htmlFor="story" className="text-base font-semibold">
                        {t('form.tellUs')} *
                      </Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <HelpCircle className="w-4 h-4 text-gray-400" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs">{t('form.tellUsHelp')}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <Textarea
                      id="story"
                      placeholder={t('form.tellUsPlaceholder')}
                      value={formData.story}
                      onChange={(e) => setFormData({ ...formData, story: e.target.value })}
                      rows={6}
                      maxLength={2000}
                      className="border-gray-300 focus:border-purple-500 focus:ring-purple-500 resize-none"
                    />
                    <p className="text-xs text-gray-500 mt-1 text-right">
                      {formData.story.length} / 2000
                    </p>
                  </div>

                </div>
              </CardContent>
            </Card>

            {/* Bouton Continuer Étape 1 - Pleine largeur */}
            <div className="w-full">
              <Button
                type="button"
                onClick={() => {
                  // Passer à l'étape 2
                  setCurrentStep(2);
                  // Scroll vers le haut de la page pour l'étape 2
                  setTimeout(() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }, 100);
                }}
                className="w-full btn-premium hover:from-purple-700 hover:to-fuchsia-600 text-white font-semibold py-6 text-lg shadow-lg hover:shadow-xl transition-all"
              >
                Continuer →
              </Button>
            </div>
          </div>
          )}

          {/* ÉTAPE 2 : Finalisation & Paiement */}
          {currentStep === 2 && (
          <div className="animate-fadeIn">
            {/* Formulaire Coordonnées */}
            <Card className="glass-card soft-shadow-lg">
              <CardContent className="p-6 md:p-8">
                {/* Bouton Retour en haut */}
                <div className="mb-6">
                  <Button
                    type="button"
                    onClick={() => {
                      setCurrentStep(1);
                      // Scroll vers le formulaire
                      const formElement = document.querySelector('#create');
                      if (formElement) {
                        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                    variant="outline"
                    className="border-gray-300 hover:border-purple-500 hover:bg-purple-50 transition-all"
                  >
                    ← Retour
                  </Button>
                </div>

                <div className="space-y-6">

                  {/* Email Address */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Label htmlFor="email" className="text-base font-semibold">
                        {t('form.email')} *
                      </Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <HelpCircle className="w-4 h-4 text-gray-400" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs">{t('form.emailHelp')}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <Input
                      id="email"
                      type="email"
                      placeholder={t('form.emailPlaceholder')}
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="border-gray-300 focus:border-purple-500 focus:ring-purple-500"
                    />
                  </div>

                  {/* Style & Ambiance Section */}
                  <div className="pt-6 border-t">
                    <h3 className="text-xl font-semibold text-gray-900 mb-6">
                      {t('form.styleTitle')}
                    </h3>

                    {/* Language */}
                    <div className="mb-6">
                      <div className="flex items-center gap-2 mb-2">
                        <Label className="text-base font-semibold">
                          {t('form.language')} *
                        </Label>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <HelpCircle className="w-4 h-4 text-gray-400" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="max-w-xs">{t('form.languageHelp')}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <Select value={formData.songLanguage} onValueChange={(value) => setFormData({ ...formData, songLanguage: value })}>
                        <SelectTrigger className="border-gray-300 focus:border-purple-500 focus:ring-purple-500">
                          <SelectValue placeholder={t('form.selectLanguage')} />
                        </SelectTrigger>
                        <SelectContent>
                          {languages.map((lang) => (
                            <SelectItem key={lang.value} value={lang.value}>
                              {getLabel(lang)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Musical Style */}
                    <div className="mb-6">
                      <div className="flex items-center gap-2 mb-2">
                        <Label className="text-base font-semibold">
                          {t('form.musicalStyle')} *
                        </Label>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <HelpCircle className="w-4 h-4 text-gray-400" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="max-w-xs">{t('form.musicalStyleHelp')}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <Select value={formData.musicalStyle} onValueChange={(value) => setFormData({ ...formData, musicalStyle: value })}>
                        <SelectTrigger className="border-gray-300 focus:border-purple-500 focus:ring-purple-500">
                          <SelectValue placeholder={t('form.selectStyle')} />
                        </SelectTrigger>
                        <SelectContent>
                          {musicStyles.map((style) => (
                            <SelectItem key={style.value} value={style.value}>
                              {getLabel(style)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Era */}
                    <div className="mb-6">
                      <Label className="text-base font-semibold mb-2 block">
                        {t('form.era')}
                      </Label>
                      <Select value={formData.era} onValueChange={(value) => setFormData({ ...formData, era: value })}>
                        <SelectTrigger className="border-gray-300 focus:border-purple-500 focus:ring-purple-500">
                          <SelectValue placeholder={t('form.selectEra')} />
                        </SelectTrigger>
                        <SelectContent>
                          {eras.map((era) => (
                            <SelectItem key={era.value} value={era.value}>
                              {getLabel(era)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Ambiance */}
                    <div className="mb-6">
                      <div className="flex items-center gap-2 mb-2">
                        <Label className="text-base font-semibold">
                          {t('form.ambiance')}
                        </Label>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <HelpCircle className="w-4 h-4 text-gray-400" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="max-w-xs">{t('form.ambianceHelp')}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <Select value={formData.ambiance} onValueChange={(value) => setFormData({ ...formData, ambiance: value })}>
                        <SelectTrigger className="border-gray-300 focus:border-purple-500 focus:ring-purple-500">
                          <SelectValue placeholder={t('form.selectAmbiance')} />
                        </SelectTrigger>
                        <SelectContent>
                          {ambiances.map((amb) => (
                            <SelectItem key={amb.value} value={amb.value}>
                              {getLabel(amb)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Voice */}
                    <div className="mb-6">
                      <div className="flex items-center gap-2 mb-3">
                        <Label className="text-base font-semibold">
                          {t('form.voice')} *
                        </Label>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <HelpCircle className="w-4 h-4 text-gray-400" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="max-w-xs">{t('form.voiceHelp')}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <RadioGroup value={formData.voice} onValueChange={(value) => setFormData({ ...formData, voice: value })} className="flex flex-wrap gap-4">
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="male" id="male" />
                          <Label htmlFor="male" className="cursor-pointer">{t('form.voiceMale')}</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="female" id="female" />
                          <Label htmlFor="female" className="cursor-pointer">{t('form.voiceFemale')}</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="duo" id="duo" />
                          <Label htmlFor="duo" className="cursor-pointer">{t('form.voiceDuo')} (+$9.90)</Label>
                        </div>
                      </RadioGroup>
                    </div>

                    {/* Voice Recording - JUSTE APRÈS Artist Voice */}
                    <div className="mb-6 p-6 border-2 border-orange-200 rounded-xl bg-gradient-to-r from-orange-50 to-amber-50">
                      <div className="flex items-center gap-3 mb-3">
                        <Switch
                          checked={formData.hasVoiceRecording}
                          onCheckedChange={(checked) => setFormData({ ...formData, hasVoiceRecording: checked })}
                        />
                        <Label className="text-base font-bold cursor-pointer text-orange-900">
                          🎤 {t('form.voiceRecording')} (+$14.00)
                        </Label>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <HelpCircle className="w-4 h-4 text-orange-600" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-sm">
                              <p>{t('form.voiceRecordingHelp')}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>

                      {formData.hasVoiceRecording && (
                        <div className="mt-4 space-y-6 p-4 bg-white rounded-lg border border-orange-100">
                          {/* Note pour Duo */}
                          {formData.voice === 'duo' && (
                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                              <p className="text-sm text-purple-900">
                                ℹ️ <strong>{t('form.voiceRecordDuoNote')}</strong>
                              </p>
                            </div>
                          )}

                          {/* Voice 1 */}
                          <div className="border-2 border-orange-200 rounded-lg p-4 bg-orange-50/50">
                            <div className="flex items-center gap-2 mb-4">
                              <div className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold">
                                1
                              </div>
                              <h4 className="font-semibold text-gray-900">
                                {t('form.voiceRecordVoice1')}
                              </h4>
                            </div>

                            {/* Nom de la personne */}
                            <div className="mb-4">
                              <Label className="text-sm font-semibold text-gray-900 mb-2 block">
                                {t('form.voiceOwner')} *
                              </Label>
                              <Input
                                placeholder={t('form.voiceOwnerPlaceholder')}
                                value={formData.voiceRecordings[0].owner}
                                onChange={(e) => {
                                  const updated = [...formData.voiceRecordings];
                                  updated[0].owner = e.target.value;
                                  setFormData({ ...formData, voiceRecordings: updated });
                                }}
                                className="border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                              />
                            </div>

                            {/* Enregistreur vocal */}
                            <VoiceRecorder 
                              onRecordingComplete={(blob) => {
                                const updated = [...formData.voiceRecordings];
                                updated[0].recording = blob;
                                setFormData({ ...formData, voiceRecordings: updated });
                              }}
                              t={t}
                            />
                          </div>

                          {/* Voice 2 - Seulement si Duo */}
                          {formData.voice === 'duo' && (
                            <div className="border-2 border-orange-200 rounded-lg p-4 bg-orange-50/50">
                              <div className="flex items-center gap-2 mb-4">
                                <div className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold">
                                  2
                                </div>
                                <h4 className="font-semibold text-gray-900">
                                  {t('form.voiceRecordVoice2')}
                                </h4>
                              </div>

                              {/* Nom de la personne */}
                              <div className="mb-4">
                                <Label className="text-sm font-semibold text-gray-900 mb-2 block">
                                  {t('form.voiceOwner')} *
                                </Label>
                                <Input
                                  placeholder={t('form.voiceOwnerPlaceholder')}
                                  value={formData.voiceRecordings[1].owner}
                                  onChange={(e) => {
                                    const updated = [...formData.voiceRecordings];
                                    updated[1].owner = e.target.value;
                                    setFormData({ ...formData, voiceRecordings: updated });
                                  }}
                                  className="border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                                />
                              </div>

                              {/* Enregistreur vocal */}
                              <VoiceRecorder 
                                onRecordingComplete={(blob) => {
                                  const updated = [...formData.voiceRecordings];
                                  updated[1].recording = blob;
                                  setFormData({ ...formData, voiceRecordings: updated });
                                }}
                                t={t}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Options Section */}
                  <div className="pt-6 border-t">
                    <h3 className="text-xl font-semibold text-gray-900 mb-6">
                      {t('form.options')}
                    </h3>

                    {/* Verses */}
                    <div className="mb-6">
                      <div className="flex items-center gap-2 mb-3">
                        <Label className="text-base font-semibold">
                          {t('form.verses')}
                        </Label>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <HelpCircle className="w-4 h-4 text-gray-400" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="max-w-xs">{t('form.versesHelp')}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <RadioGroup value={formData.verses} onValueChange={(value) => setFormData({ ...formData, verses: value })} className="flex flex-wrap gap-4">
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="2" id="verses-2" />
                          <Label htmlFor="verses-2" className="cursor-pointer">{t('common.verses2Free')}</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="3" id="verses-3" />
                          <Label htmlFor="verses-3" className="cursor-pointer">{t('common.verses3')}</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="4" id="verses-4" />
                          <Label htmlFor="verses-4" className="cursor-pointer">{t('common.verses4')}</Label>
                        </div>
                      </RadioGroup>
                    </div>

                    {/* Symbolic Phrase */}
                    <div className="mb-6">
                      <div className="flex items-center gap-3 mb-2">
                        <Switch
                          checked={formData.hasPhrase}
                          onCheckedChange={(checked) => setFormData({ ...formData, hasPhrase: checked })}
                        />
                        <Label className="text-base font-semibold cursor-pointer">
                          {t('form.phrase')} (+$2.90)
                        </Label>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <HelpCircle className="w-4 h-4 text-gray-400" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="max-w-xs">{t('form.phraseHelp')}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      {formData.hasPhrase && (
                        <Input
                          placeholder={t('common.phrasePlaceholder')}
                          value={formData.phrase}
                          onChange={(e) => setFormData({ ...formData, phrase: e.target.value })}
                          className="border-gray-300 focus:border-purple-500 focus:ring-purple-500"
                        />
                      )}
                    </div>

                    {/* Streaming */}
                    <div className="mb-6">
                      <div className="flex items-center gap-3 mb-2">
                        <Switch
                          checked={formData.streaming}
                          onCheckedChange={(checked) => setFormData({ ...formData, streaming: checked })}
                        />
                        <Label className="text-base font-semibold cursor-pointer">
                          {t('form.streaming')} (+$9.90)
                        </Label>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <HelpCircle className="w-4 h-4 text-gray-400" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="max-w-xs">{t('form.streamingHelp')}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </div>

                    {/* Delivery */}
                    <div className="mb-6">
                      <Label className="text-base font-semibold mb-3 block">
                        {t('form.delivery')} *
                      </Label>
                      <RadioGroup value={formData.delivery} onValueChange={(value) => setFormData({ ...formData, delivery: value })} className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="standard" id="delivery-standard" />
                          <Label htmlFor="delivery-standard" className="cursor-pointer">{t('form.deliveryStandard')}</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="express" id="delivery-express" />
                          <Label htmlFor="delivery-express" className="cursor-pointer">{t('form.deliveryExpress')} (+$7.90)</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="priority" id="delivery-priority" />
                          <Label htmlFor="delivery-priority" className="cursor-pointer">{t('form.deliveryPriority')} (+$12.90)</Label>
                        </div>
                      </RadioGroup>
                    </div>

                    {/* Photo */}
                    <div className="mb-6">
                      <div className="flex items-center gap-3 mb-2">
                        <Switch
                          checked={formData.hasPhoto}
                          onCheckedChange={(checked) => setFormData({ ...formData, hasPhoto: checked })}
                        />
                        <Label className="text-base font-semibold cursor-pointer">
                          {t('form.photo')} (+$2.90)
                        </Label>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <HelpCircle className="w-4 h-4 text-gray-400" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="max-w-xs">{t('form.photoHelp')}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      {formData.hasPhoto && (
                        <div className="mt-3">
                          <Button type="button" variant="outline" className="w-full">
                            <Upload className="w-4 h-4 mr-2" />
                            {t('common.uploadPhoto')}
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* AI Video CLIP */}
                    <div className="mb-6 p-6 border-2 border-purple-200 rounded-xl bg-gradient-to-r from-purple-50 via-violet-50 to-fuchsia-50">
                      <div className="flex items-center gap-3 mb-3">
                        <Switch
                          checked={formData.hasAiClip}
                          onCheckedChange={(checked) => setFormData({ ...formData, hasAiClip: checked })}
                        />
                        <Label className="text-base font-bold cursor-pointer text-purple-900">
                          🎬 {t('form.aiClip')} (+$99.00)
                        </Label>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <HelpCircle className="w-4 h-4 text-purple-600" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-sm">
                              <p>{t('form.aiClipHelp')}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      
                      {formData.hasAiClip && (
                        <div className="mt-4 space-y-4 p-4 bg-white rounded-lg border border-purple-100">
                          {/* Upload Photos - Individual slots */}
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <Label className="text-sm font-semibold text-gray-900">
                                  {t('form.aiClipPhotos')} *
                                </Label>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger>
                                      <HelpCircle className="w-4 h-4 text-gray-400" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p className="max-w-xs">{t('form.aiClipPhotosHelp')}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                              <Badge variant="outline" className="text-xs">
                                {formData.aiClipPhotos.filter(p => p.file).length} / {formData.aiClipPhotos.length} photos
                              </Badge>
                            </div>
                            
                            {/* Photo Slots */}
                            <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                              {formData.aiClipPhotos.map((photo, index) => (
                                <div key={index} className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50 hover:bg-gray-100 transition-colors">
                                  <div className="flex items-start justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                      <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-sm font-bold text-purple-600">
                                        {index + 1}
                                      </div>
                                      <span className="text-sm font-medium text-gray-700">
                                        {t('common.photoSlot')} {index + 1}
                                      </span>
                                    </div>
                                    {formData.aiClipPhotos.length > 3 && (
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => removePhotoSlot(index)}
                                        className="text-red-600 hover:text-red-700 -mt-2"
                                      >
                                        <X className="w-4 h-4" />
                                      </Button>
                                    )}
                                  </div>

                                  {/* Context Input - AVANT l'upload */}
                                  <div className="mb-3">
                                    <Label className="text-xs text-gray-600 mb-1 block">
                                      {t('form.aiClipPhotoName')} *
                                    </Label>
                                    <Input
                                      placeholder={t('form.aiClipPhotoNamePlaceholder')}
                                      value={photo.context}
                                      onChange={(e) => updatePhotoContext(index, e.target.value)}
                                      className="text-sm border-gray-300 focus:border-purple-500 focus:ring-purple-500"
                                    />
                                  </div>

                                  {/* Photo Upload */}
                                  {!photo.file ? (
                                    <div>
                                      <input
                                        type="file"
                                        id={`photo-upload-${index}`}
                                        accept="image/jpeg,image/png,image/heic"
                                        onChange={(e) => handlePhotoUpload(index, e.target.files[0])}
                                        className="hidden"
                                      />
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="w-full border-dashed border-2 border-purple-300 hover:border-purple-500 hover:bg-purple-50"
                                        onClick={() => document.getElementById(`photo-upload-${index}`).click()}
                                      >
                                        <Upload className="w-4 h-4 mr-2" />
                                        {t('common.uploadPhoto')}
                                      </Button>
                                      <p className="text-xs text-gray-500 mt-1">
                                        {t('common.fileFormat')}
                                      </p>
                                    </div>
                                  ) : (
                                    <div className="relative">
                                      <img
                                        src={photo.preview}
                                        alt={`Photo ${index + 1}`}
                                        className="w-full h-32 object-cover rounded-lg"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => removePhoto(index)}
                                        className="absolute top-2 right-2 w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg"
                                      >
                                        <X className="w-4 h-4" />
                                      </button>
                                      <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                                        ✓ {t('common.uploaded')}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>

                            {/* Add More Photos Button */}
                            {formData.aiClipPhotos.length < 10 && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={addPhotoSlot}
                                className="w-full mt-3 border-purple-300 text-purple-600 hover:bg-purple-50"
                              >
                                {t('common.addPhotoButton')}
                              </Button>
                            )}

                            {formData.aiClipPhotos.filter(p => p.file).length < 3 && (
                              <p className="text-xs text-amber-600 mt-2">
                                ⚠️ {t('common.minPhotosWarning')}
                              </p>
                            )}
                          </div>

                          {/* Video Scenario */}
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <Label htmlFor="aiClipScenario" className="text-sm font-semibold text-gray-900">
                                {t('form.aiClipScenario')} *
                              </Label>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <HelpCircle className="w-4 h-4 text-gray-400" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p className="max-w-xs">{t('form.aiClipScenarioHelp')}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                            <Textarea
                              id="aiClipScenario"
                              placeholder={t('form.aiClipScenarioPlaceholder')}
                              value={formData.aiClipScenario}
                              onChange={(e) => setFormData({ ...formData, aiClipScenario: e.target.value })}
                              rows={4}
                              maxLength={500}
                              className="border-gray-300 focus:border-purple-500 focus:ring-purple-500 resize-none"
                            />
                            <p className="text-xs text-gray-500 mt-1 text-right">
                              {formData.aiClipScenario.length} / 500
                            </p>
                          </div>

                          {/* Info Badge */}
                          <div className="bg-purple-100 border border-purple-200 rounded-lg p-3">
                            <p className="text-xs text-purple-900">
                              <strong>{t('common.whatYouGet')}</strong>
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Price Summary */}
                  <div className="pt-6 border-t">
                    <div className="bg-gradient-to-r from-purple-50 via-violet-50 to-fuchsia-50 rounded-xl p-6">
                      {/* Delivery Time Info */}
                      <div className="mb-6 p-4 bg-white rounded-lg border border-purple-200">
                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                          ⏱️ {t('form.deliveryInfo')}
                        </h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-green-500"></div>
                            <span className="text-gray-700">{t('form.deliveryStandard8h')}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                            <span className="text-gray-700">{t('form.deliveryWithClip16h')}</span>
                          </div>
                        </div>
                      </div>

                      {/* Récapitulatif - Déplacé ici en bas */}
                      <div className="mt-8 p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 rounded-2xl border-2 border-purple-200 shadow-lg">
                        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                          📝 Récapitulatif de votre chanson
                        </h3>
                        <div className="grid md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="font-semibold text-gray-700">Pour :</span> <span className="text-gray-900">{formData.whoFor || 'Non spécifié'}</span>
                          </div>
                          <div>
                            <span className="font-semibold text-gray-700">Relation :</span> <span className="text-gray-900">{formData.relationship || 'Non spécifié'}</span>
                          </div>
                          {formData.occasion && (
                            <div>
                              <span className="font-semibold text-gray-700">Occasion :</span> <span className="text-gray-900">{formData.occasion}</span>
                            </div>
                          )}
                          <div>
                            <span className="font-semibold text-gray-700">Langue :</span> <span className="text-gray-900">{formData.songLanguage?.toUpperCase() || 'EN'}</span>
                          </div>
                          <div>
                            <span className="font-semibold text-gray-700">Style :</span> <span className="text-gray-900">{formData.musicalStyle || 'Non spécifié'}</span>
                          </div>
                          <div>
                            <span className="font-semibold text-gray-700">Ambiance :</span> <span className="text-gray-900">{formData.ambiance || 'Non spécifié'}</span>
                          </div>
                          <div>
                            <span className="font-semibold text-gray-700">Voix :</span> <span className="text-gray-900">{formData.voice === 'male' ? 'Homme' : formData.voice === 'female' ? 'Femme' : 'Duo'}</span>
                          </div>
                          <div>
                            <span className="font-semibold text-gray-700">Couplets :</span> <span className="text-gray-900">{formData.verses} couplets</span>
                          </div>
                          {formData.hasPhrase && (
                            <div className="md:col-span-2">
                              <span className="font-semibold text-gray-700">Phrase symbolique :</span> <span className="text-gray-900 italic">"{formData.phrase}"</span>
                            </div>
                          )}
                          <div>
                            <span className="font-semibold text-gray-700">Streaming :</span> <span className="text-gray-900">{formData.streaming ? 'Oui' : 'Non'}</span>
                          </div>
                          <div>
                            <span className="font-semibold text-gray-700">Livraison :</span> <span className="text-gray-900">{formData.delivery === 'standard' ? 'Standard (48h)' : formData.delivery === 'express' ? 'Express (24h)' : 'Prioritaire (12h)'}</span>
                          </div>
                          {formData.hasPhoto && (
                            <div>
                              <span className="font-semibold text-gray-700">Photo :</span> <span className="text-gray-900">✓ Incluse</span>
                            </div>
                          )}
                          {formData.hasVoiceRecording && (
                            <div>
                              <span className="font-semibold text-gray-700">Enregistrement vocal :</span> <span className="text-gray-900">✓ Inclus</span>
                            </div>
                          )}
                          {formData.hasAiClip && (
                            <div>
                              <span className="font-semibold text-gray-700">Clip IA :</span> <span className="text-gray-900">✓ Inclus ({formData.aiClipPhotos.filter(p => p.file).length} photos)</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex justify-between items-center mb-4 mt-6">
                        <span className="text-lg font-semibold text-gray-900">{t('common.totalPrice')}</span>
                        <span className="text-3xl font-bold bg-gradient-to-r from-purple-600 via-violet-500 to-fuchsia-500 bg-clip-text text-transparent">
                          ${calculatePrice()}
                        </span>
                      </div>
                      <Button type="submit" className="w-full btn-premium hover:from-blue-700 hover:to-cyan-600 text-white font-semibold py-6 text-lg shadow-lg hover:shadow-xl transition-all">
                        {t('form.proceedToPayment')} →
                      </Button>
                      
                      {/* Secure Payment Section */}
                      <div className="mt-6 text-center">
                        <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center justify-center gap-2">
                          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                          <span>{t('form.securePayment')}</span>
                        </p>
                        <div className="flex items-center justify-center gap-2 flex-wrap text-xs font-semibold text-gray-600">
                          <span className="bg-white px-3 py-1.5 rounded shadow-sm border border-gray-200">VISA</span>
                          <span className="bg-white px-3 py-1.5 rounded shadow-sm border border-gray-200">MASTERCARD</span>
                          <span className="bg-white px-3 py-1.5 rounded shadow-sm border border-gray-200">AMEX</span>
                          <span className="bg-white px-3 py-1.5 rounded shadow-sm border border-gray-200">APPLE PAY</span>
                          <span className="bg-white px-3 py-1.5 rounded shadow-sm border border-gray-200">CB</span>
                        </div>
                      </div>
                      
                      <p className="text-xs text-center text-gray-600 mt-4">
                        {t('form.orderNow')}
                      </p>
                    </div>
                  </div>

                  {/* Bouton Retour */}
                  <div className="pt-6 border-t">
                    <Button
                      type="button"
                      onClick={() => {
                        setCurrentStep(1);
                        // Scroll vers le formulaire
                        const formElement = document.querySelector('#create');
                        if (formElement) {
                          formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }}
                      variant="outline"
                      className="w-full md:w-auto border-gray-300 hover:border-purple-500 hover:bg-purple-50 transition-all"
                    >
                      ← Retour
                    </Button>
                  </div>

                </div>
              </CardContent>
            </Card>
          </div>
          )}
        </form>
      </div>
    </section>
  );
};

export default SongForm;
